'use client';

import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Select } from '@app/app/_components/Input/Select/Select';
import { Input } from '@app/app/_components/Input/Input';
import { TestChoiceButtonInput } from '@app/app/_components/Input/TestChoiceButtonInput';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { CommonType } from '@app/domain/model/CommonType';
import { useTranslations } from 'next-intl';
import { LifeProducts } from '@app/domain/model/eshop/life/LifeProducts';
import { useMemo } from 'react';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { InsuranceBeneficiaryDetailsView } from '@app/app/esales/life/_sections/steps/TaxInformationStep/InsuranceBeneficiaryDetailsView';
import { LegalOriginOfMoneyDetailsView } from '@app/app/esales/life/_sections/steps/TaxInformationStep/LegalOriginOfMoneyDetailsView';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';

interface Props {
    violations?: ViolationMap;
    lifeProducts: LifeProducts;
    sessionData?: Nullable<SessionResponse>;
}

export function TaxInformationStep({ violations, lifeProducts, sessionData }: Props) {
    const t = useTranslations();

    const countryOptions = useMemo(
        () => lifeProducts.countries.map(country => ({ value: country.code, label: country.value })),
        [lifeProducts.countries],
    );

    const defaultNationalBackground = useMemo(() => {
        if (sessionData?.thirdStep?.hasNationalBackground === undefined) return;

        if (sessionData?.thirdStep?.hasNationalBackground) return CommonType.Yes;

        if (!sessionData?.thirdStep?.hasNationalBackground) return CommonType.No;
    }, [sessionData?.thirdStep?.hasNationalBackground]);

    return (
        <div className="flex flex-col gap-6 max-sm:mb-20">
            <Select
                name="countryOfResidence"
                label={t('eshop.life.countryOfResidence')}
                error={violations?.countryOfResidence}
                placeholder={t('eshop.life.countryOfResidencePlaceholder')}
                options={countryOptions}
                defaultValue={sessionData?.thirdStep?.countryOfResidence}
                searchable
            />

            <Select
                name="citizenship"
                label={t('eshop.life.citizenship')}
                error={violations?.citizenship}
                placeholder={t('eshop.life.citizenshipPlaceholder')}
                options={countryOptions}
                defaultValue={sessionData?.thirdStep?.citizenship}
                searchable
            />

            <Select
                name="taxResidency"
                label={t('eshop.life.taxResidency')}
                labelAddon={
                    <InformationPopover content={<Paragraph>{t('eshop.life.taxResidencyTooltip')}</Paragraph>}>
                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                    </InformationPopover>
                }
                labelClassName="justify-between"
                error={violations?.taxResidency}
                placeholder={t('eshop.life.taxResidencyPlaceholder')}
                options={countryOptions}
                defaultValue={sessionData?.thirdStep?.taxResidency}
                searchable
            />

            <Select
                name="countryOfBirth"
                label={t('eshop.life.countryOfBirth')}
                error={violations?.countryOfBirth}
                placeholder={t('eshop.life.countryOfBirthPlaceholder')}
                options={countryOptions}
                defaultValue={sessionData?.thirdStep?.countryOfBirth}
                searchable
            />

            <Input
                name="idCardOrPassportNumber"
                label={t('eshop.life.idCardOrPassportNumber')}
                placeholder={t('eshop.life.idCardOrPassportNumberPlaceholder')}
                error={violations?.idCardOrPassportNumber}
                defaultValue={sessionData?.thirdStep?.idCardOrPassportNumber}
            />

            <div className="flex flex-col gap-2">
                <label className="flex items-center justify-between gap-1 text-sm">
                    {t('eshop.life.hasNationalBackground')}
                    <InformationPopover content={<Paragraph>{t('eshop.life.hasNationalBackgroundTooltip')}</Paragraph>}>
                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                    </InformationPopover>
                </label>
                <TestChoiceButtonInput
                    name="hasNationalBackground"
                    error={violations?.hasNationalBackground}
                    options={[
                        { value: CommonType.No, label: t('general.no') },
                        { value: CommonType.Yes, label: t('general.yes') },
                    ]}
                    defaultChecked={defaultNationalBackground}
                />
            </div>

            <Input
                name="employer"
                label={t('eshop.life.yourEmployer')}
                placeholder={t('eshop.life.yourEmployerPlaceholder')}
                error={violations?.employer}
                defaultValue={sessionData?.thirdStep?.employer}
            />

            <div className="flex flex-row items-start justify-between gap-10">
                <CheckboxInput
                    name="insuranceForSelfAndFamily"
                    label={t('eshop.life.insuranceForSelfAndFamily')}
                    error={violations?.insuranceForSelfAndFamily}
                />
                <InformationPopover content={<InsuranceBeneficiaryDetailsView />}>
                    <QuestionCircleIcon className="size-4 text-neutral-500" />
                </InformationPopover>
            </div>

            <div className="flex flex-row items-start justify-between gap-10">
                <CheckboxInput
                    name="legalOriginOfMoney"
                    label={t('eshop.life.legalOriginOfMoney')}
                    error={violations?.legalOriginOfMoney}
                />
                <InformationPopover content={<LegalOriginOfMoneyDetailsView />}>
                    <QuestionCircleIcon className="size-4 text-neutral-500" />
                </InformationPopover>
            </div>
        </div>
    );
}
